import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { MarketSeed } from '@/seed/markets';

/**
 * MarketCard — renders one seed (or future live) market entry.
 *
 * Phase 3: accepts MarketSeed prop. Phase 4 will swap for the mapped MarketResponse
 * type when useMarkets() replaces MARKETS_SEED — DOM structure stays the same.
 *
 * YES/NO bar uses inline flex proportioning (matching design's `flex: m.yes` pattern).
 * YES/NO bar uses rounded-pill per RAD-01 brand radius override (UAT gap closure).
 * Card border is border-rule-strong (Phase 1 token); card itself stays unrounded so
 * the right/bottom borders form the visual grid lines.
 *
 * Volume formatted as € + toLocaleString('en-US') (e.g., €84,210).
 * closeLabel is a pre-formatted string from the seed (Phase 4 will derive from eventStart).
 */

type MarketCardProps = {
  market: MarketSeed;
};

export default function MarketCard({ market }: MarketCardProps): JSX.Element {
  const { t } = useTranslation();
  const yesCents = Math.round(market.lastPriceYes * 100);
  const noCents = 100 - yesCents;
  const yesLabel =
    yesCents >= 25 ? `${t('markets.card.yesPrefix')} · ${yesCents}¢` : `${yesCents}¢`;
  const noLabel = noCents >= 25 ? `${t('markets.card.noPrefix')} · ${noCents}¢` : `${noCents}¢`;
  const volume = `€${market.volumeEur.toLocaleString('en-US')}`;

  return (
    <article className="border-rule-strong bg-paper hover:bg-paper-2 cursor-pointer border-r border-b p-7 px-6 transition-colors">
      <div className="text-dim mb-4 flex justify-between font-mono text-[10px] tracking-[0.22em] uppercase">
        <span>{market.category}</span>
        {market.live ? (
          <span className="text-red inline-flex items-center gap-1.5">
            <span className="rounded-pill bg-red h-1.5 w-1.5" aria-hidden="true" />
            {t('markets.card.live')}
          </span>
        ) : (
          <span>{t('markets.card.open')}</span>
        )}
      </div>
      <p className="text-ink m-0 mb-5 min-h-[60px] font-sans text-[17px] leading-[1.35] tracking-[-0.01em]">
        {market.title}
      </p>
      <div className="rounded-pill mb-4 flex h-9 overflow-hidden font-mono text-[11px] font-semibold tracking-[0.05em]">
        <div
          className="bg-orange text-ink flex min-w-[40px] items-center justify-center"
          style={{ flex: yesCents }}
        >
          {yesLabel}
        </div>
        <div
          className="bg-ink text-paper flex min-w-[40px] items-center justify-center"
          style={{ flex: noCents }}
        >
          {noLabel}
        </div>
      </div>
      <div className="text-dim flex justify-between font-mono text-[10px] tracking-[0.18em] uppercase">
        <span>
          {t('markets.card.volumeLabel')} {volume}
        </span>
        <span>{market.closeLabel}</span>
      </div>
    </article>
  );
}
